import React, { useLayoutEffect, useRef, useState } from "react";
import classNames from "classnames";
import { Logo } from "../../brand";
import { usePageScrollPosition, useWindowDimensions } from "../../utils";
import { InspirationDropdown } from "..";
import { AboutDropdown } from "../AboutDropdown/AboutDropdown";
import { Link, LinkButton } from "../..";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import { DestinationDropdown } from "../DestinationDropdown/DestinationDropdown";
import "./Header.scss";

interface Props {
  /**
   * The current URL, sourced from react-router or Next's useRouter().
   * Used to determine visibility and target URL of Enquire CTA.
   */
  currentPath?: string;

  /**
   * The currently signed in user
   */
  user?: IUser;

  className?: string;

  /**
   * Regions to show in the destinations dropdown
   */
  regions?: ILocationSummary[];

  /**
   * Countries to show in the destinations dropdown
   */
  countries?: ILocationSummary[];

  /**
   * Content to show in the inspiration dropdown
   */
  inspirationContent?: [IContent, IContent, IContent];
}

/**
 * Header of the marketing site.
 * 
 * Key features:
 * - fixed to the top of the viewport [1]
 * - collapses when viewport is scrolled
 * - inserts a "spacing" div of the same height into the page to avoid overlapping the content below.
 * 
 * [1] Stories are rendered within `iframe`s so `position: fixed` doesn't work the same in Storybook. There is some extra CSS to simulate how it works for documentation purposes.
 * 
 * @status development
 * - change enquiry form route based on page
 * - mobile styling + mobile menu
 * - add currency selector
 */
export const Header: React.FC<Props> = (props) => {

  // We position the header element using position: fixed. To prevent it permanently overlapping the content below,
  // we insert a "spacing" element next to it of the same height. The initial value provided here is replaced with
  // the actual height after the first client render and when resizing to/ from mobile.
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [spacerHeight, setSpacerHeight] = useState(123);

  const { isMobile } = useWindowDimensions();
  const scrollPosition = usePageScrollPosition();
  const isAtTop = scrollPosition.y < spacerHeight;

  useLayoutEffect(() => {
    if (headerRef.current) {
      const dim = headerRef.current.getBoundingClientRect();
      setSpacerHeight(dim.height);
    }
  }, [isMobile]);

  const showEnquireCta = !props.currentPath?.includes(WebRoutes.customerRoutePrefix);

  return (
    <>
      <div className="marketing-header__spacer" style={{ height: spacerHeight }} />
      <div
        className={classNames("marketing-header", props.className, {
          "marketing-header--fixed": !isAtTop && !isMobile
        })}
        ref={headerRef}
      >
        <div className="marketing-header__wrap">
          <div className="level marketing-header__extras is-marginless is-hidden-mobile">
            <div className="level-left">
              <div className="level-item">
                <a
                  href="http://www.reviews.co.uk/company-reviews/store/travellocalcom"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    className="review-stars"
                    alt="Check out our great reviews from reviews.co.uk"
                    src="/images/sticky-footer/reviews-stars-inline.png"
                    width="87.5"
                    height="15"
                  />
                  <img
                    className="review-logo"
                    alt="Logo of reviews.co.uk"
                    src="/images/sticky-footer/reviews-logo-inline.png"
                    width="90"
                    height="15"
                  />
                </a>

              </div>
              <div className="level-item">
                <span className="text--label text--dark">ABTA &amp; ATOL protected</span>
              </div>
            </div>

            <div className="level-right">
              <div className="level-item">
                <LinkButton
                  buttonClass="secondary-button"
                  to={WebRoutes.aboutHowItWorks()}
                  className="text--label text--dark is-marginless"
                >
                  How it works
                </LinkButton>
              </div>
              <div className="level-item">
                <LinkButton
                  buttonClass="secondary-button"
                  to="tel:+441173257898"
                  className="text--label text--dark is-marginless"
                >
                  +44 (0)117 325 7898
                </LinkButton>
              </div>
              {props.user &&
                <div className="level-item">
                  <LinkButton
                    buttonClass="secondary-button"
                    to={WebRoutes.logout()}
                    className="text--label text--dark is-marginless"
                  >
                    Sign out
                  </LinkButton>
                </div>
              }
            </div>
          </div>

          <nav className="level is-mobile is-marginless marketing-header__nav" role="navigation" aria-label="Main menu">
            <div className="level-left">
              <Link to={WebRoutes.index()}>
                <Logo className="marketing-header__logo" width={103} />
              </Link>
            </div>

            <div className="level-right">
              <div className="level-item is-hidden-mobile">
                <DestinationDropdown regions={props.regions} countries={props.countries} />
              </div>
              <div className="level-item is-hidden-mobile">
                <InspirationDropdown content={props.inspirationContent} />
              </div>
              <div className="level-item is-hidden-mobile">
                <AboutDropdown />
              </div>

              {showEnquireCta &&
                <div className="level-item">
                  <LinkButton buttonClass="primary-button" to={WebRoutes.enquiryForm({ campaign: "header" })}>
                    Enquire
                  </LinkButton>
                </div>
              }

              {!props.user &&
                <div className="level-item is-hidden-mobile">
                  <LinkButton
                    buttonClass="primary-button"
                    className="inverted"
                    to={WebRoutes.login()}
                  >
                    Sign in
                  </LinkButton>
                </div>
              }

              {props.user &&
                <div className="level-item is-hidden-mobile">
                  <LinkButton
                    buttonClass="primary-button"
                    className="inverted"
                    to={WebRoutes.customerIndex()}
                  >
                    My trips
                  </LinkButton>
                </div>
              }
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
